import { useState, useEffect } from 'react'
import { Download, X, Smartphone } from 'lucide-react'

export default function PWAInstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState(null)
    const [showInstallPrompt, setShowInstallPrompt] = useState(false)
    const [isIOS, setIsIOS] = useState(false)
    const [isInstalled, setIsInstalled] = useState(false)

    useEffect(() => {
        // Check if already installed
        if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
            setIsInstalled(true)
            return
        }

        // Check if dismissed before
        const dismissed = localStorage.getItem('pwa-install-dismissed')
        if (dismissed) {
            const dismissedTime = parseInt(dismissed)
            const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24)
            if (daysSinceDismissed < 7) {
                return // Don't show again for 7 days
            }
        }

        // Detect iOS
        const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
        setIsIOS(iOS)

        // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault()
            setDeferredPrompt(e)

            // Show prompt after 5 seconds
            setTimeout(() => {
                setShowInstallPrompt(true)
            }, 5000)
        }

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

        // For iOS, show manual instructions after 5 seconds
        if (iOS) {
            setTimeout(() => {
                setShowInstallPrompt(true)
            }, 5000)
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
        }
    }, [])

    const handleInstallClick = async () => {
        if (!deferredPrompt) return

        // Show the install prompt
        deferredPrompt.prompt()

        // Wait for the user to respond to the prompt
        const { outcome } = await deferredPrompt.userChoice

        if (outcome === 'accepted') {
            console.log('User accepted the install prompt')
        } else {
            console.log('User dismissed the install prompt')
        }

        // Clear the deferredPrompt
        setDeferredPrompt(null)
        setShowInstallPrompt(false)
    }

    const handleDismiss = () => {
        setShowInstallPrompt(false)
        localStorage.setItem('pwa-install-dismissed', Date.now().toString())
    }

    if (isInstalled || !showInstallPrompt) {
        return null
    }

    return (
        <>
            {/* Desktop/Android Install Prompt */}
            {!isIOS && deferredPrompt && (
                <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 animate-slide-up">
                    <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
                                    <Smartphone className="w-6 h-6 text-blue-600" />
                                </div>
                                <div className="text-white">
                                    <h3 className="font-bold text-lg">Cài đặt DelyTour</h3>
                                    <p className="text-sm text-blue-100">Trải nghiệm như app native!</p>
                                </div>
                            </div>
                            <button
                                onClick={handleDismiss}
                                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-4">
                            <ul className="space-y-2 mb-4 text-sm text-gray-600">
                                <li className="flex items-center gap-2">
                                    <span className="text-green-500">✓</span>
                                    Truy cập nhanh từ màn hình chính
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="text-green-500">✓</span>
                                    Hoạt động ngoại tuyến (offline)
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="text-green-500">✓</span>
                                    Nhận thông báo tour mới
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="text-green-500">✓</span>
                                    Trải nghiệm như ứng dụng thật
                                </li>
                            </ul>

                            <button
                                onClick={handleInstallClick}
                                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                            >
                                <Download className="w-5 h-5" />
                                Cài đặt ngay
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* iOS Install Instructions */}
            {isIOS && (
                <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 animate-slide-up">
                    <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
                                    <Smartphone className="w-6 h-6 text-blue-600" />
                                </div>
                                <div className="text-white">
                                    <h3 className="font-bold text-lg">Cài đặt DelyTour</h3>
                                    <p className="text-sm text-blue-100">Trên iPhone/iPad</p>
                                </div>
                            </div>
                            <button
                                onClick={handleDismiss}
                                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-4">
                            <p className="text-sm text-gray-600 mb-3">
                                Để cài đặt app trên iPhone/iPad:
                            </p>
                            <ol className="space-y-2 text-sm text-gray-700 mb-4">
                                <li className="flex gap-2">
                                    <span className="font-bold text-blue-600">1.</span>
                                    <span>Nhấn nút <strong>Chia sẻ</strong> <span className="text-xl">⎙</span> ở thanh công cụ</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="font-bold text-blue-600">2.</span>
                                    <span>Chọn <strong>"Thêm vào Màn hình chính"</strong> <span className="text-xl">➕</span></span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="font-bold text-blue-600">3.</span>
                                    <span>Nhấn <strong>"Thêm"</strong> để hoàn tất</span>
                                </li>
                            </ol>

                            <button
                                onClick={handleDismiss}
                                className="w-full bg-gray-200 text-gray-700 py-3 px-6 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                            >
                                Đã hiểu
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .animate-slide-up {
          animation: slide-up 0.4s ease-out;
        }
      `}</style>
        </>
    )
}

